/*
 * File: PatientSelectionCard.js
 * Project: mp-mobile-app
 *
 * Created by Jennifer Loecker on 09/17/2019
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Shows and styles the information for the selected patient when creating an appointment
 */

import React from "react";
import PatientCard from "services/patients/patient_card/PatientCard";
import PatientStore from "services/patients/store/PatientStore";
import UIStore from "../store/UIStore";
import { observer } from "mobx-react";
import { ListItem } from "@material-ui/core";
import ProviderOfficeFeeModel from "services/patients/demographics/provider_office_fee/store/ProviderOfficeFeeModel";
import { StoreContext } from "utils/useStore";
import { Instance, SnapshotOut } from "mobx-state-tree";
import Preferences from "services/preferences/Store";
import PatientModel from "services/patients/demographics/patient_info/store/DemographicsModel";

export interface Props {
  parentCallBack: (
    model: SnapshotOut<typeof PatientModel> | undefined,
    id: number | undefined
  ) => void;
}
type State = {
  loading: boolean;
  patientModel?: SnapshotOut<typeof PatientModel>;
  providerOfficeModel?: Instance<typeof ProviderOfficeFeeModel>;
};

class PatientSelectionCard extends React.Component<Props, State> {
  state: State = { loading: true };
  patientStore: Instance<typeof PatientStore> | undefined;
  static contextType = StoreContext;

  // Note: The "correct" way to do this is with `declare`, but Create React App isn't configured correctly for that to work at the moment.
  context!: React.ContextType<typeof StoreContext>;

  async componentDidMount() {
    await this.setPatientModel();
    this.setState(prev => ({ ...prev, loading: false }));
  }

  setPatientModel = async () => {
    if (this.context === undefined) {
      throw new Error("Missing context in PatientSelectionCard");
    }
    const patientStore = this.context.getStore(PatientStore);
    this.patientStore = patientStore;
    const patientModel = await this.patientStore?.patientCard(
      this.context.getStore(Preferences)
    );
    const providerOfficeModel = this.context.getStore(ProviderOfficeFeeModel);
    await providerOfficeModel.getProviderOfficeFeeData();

    this.setState(prev => ({ ...prev, patientModel, providerOfficeModel }));
  };

  patientCardSelected = () => {
    const providerId = this.state.providerOfficeModel?.patientProvider.userId;
    this.props.parentCallBack(this.state.patientModel, providerId);
    this.context?.getStore(UIStore).togglePatientSelection();
  };

  render() {
    return this.state.patientModel && this.state.patientModel.patientId ? (
      <React.Fragment>
        <ListItem
          style={{ flexWrap: "wrap" }}
          button
          onClick={this.patientCardSelected}
        >
          <PatientCard patient={this.state.patientModel} />
        </ListItem>
      </React.Fragment>
    ) : null;
  }
}

export default observer(PatientSelectionCard);
